import { useState, useRef, useEffect } from "react";

interface Props {
  fieldId?: string;
  label?: string;
  helper?: string;
  options: string[];
  /** Optional parallel array of storage keys. When provided, onChange returns values[i]
   *  and the current value is resolved from values to display the matching option label. */
  values?: string[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

export default function SearchableDropdown({ fieldId, label, helper, options, values, value, onChange, placeholder = "" }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const listboxId = useRef(`sd-listbox-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const displayLabel = values
    ? (() => {
        const idx = values.indexOf(value);
        return idx >= 0 ? options[idx] : "";
      })()
    : value;

  const entries = options.map((lbl, i) => ({
    label: lbl,
    key: values ? values[i] : lbl,
  }));

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = entries
    .filter(({ label: lbl }) => {
      if (!normalizedQuery) return true;
      return lbl.toLowerCase().startsWith(normalizedQuery);
    });

  const handleSelect = (key: string) => {
    onChange(key);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={ref} className={`relative min-w-0 w-full ${open ? "z-50" : "z-20"}`} id={fieldId}>
      {label && (
        <label className="block text-[13px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
          {label}
        </label>
      )}
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId.current}
        aria-label={label}
        onClick={() => {
          setOpen(!open);
          setQuery("");
        }}
        className={`w-full h-12 flex items-center justify-between px-4 bg-white border rounded-xl text-sm transition-colors cursor-pointer ${
          displayLabel ? "border-emerald-400 text-slate-900" : "border-slate-200 text-slate-400"
        } ${open ? "ring-2 ring-emerald-400 ring-offset-0 border-transparent" : ""}`}
      >
        <span className={displayLabel ? "text-slate-900" : "text-slate-400"}>{displayLabel || placeholder}</span>
        <div className="flex items-center gap-1.5 shrink-0">
          {displayLabel && <i className="ri-checkbox-circle-fill text-emerald-500 text-base" />}
          <i className={`ri-arrow-${open ? "up" : "down"}-s-line text-slate-400 text-base`} />
        </div>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
              <i className="ri-search-line text-slate-400 text-sm" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={placeholder}
                autoFocus
                aria-label="Search"
                style={{ fontSize: "16px", touchAction: "manipulation" }}
                className="flex-1 min-w-0 bg-transparent text-slate-900 placeholder:text-slate-400 border-0 focus:border-0 focus:ring-0 focus:outline-none"
              />
              {query && (
                <button type="button" onClick={() => setQuery("")} className="cursor-pointer shrink-0" aria-label="Limpiar búsqueda">
                  <i className="ri-close-line text-slate-400 text-sm" />
                </button>
              )}
            </div>
          </div>
          <div id={listboxId.current} role="listbox" aria-label={label} className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-400 text-center">Sin resultados</div>
            ) : (
              filtered.map(({ label: optLabel, key }) => (
                <button
                  key={key}
                  type="button"
                  role="option"
                  aria-selected={key === value}
                  onClick={() => handleSelect(key)}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                    key === value ? "bg-emerald-50 text-emerald-700 font-medium" : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {key === value && <i className="ri-check-line mr-2 text-emerald-600" />}
                  {optLabel}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {helper && <p className="text-[13px] text-slate-400 mt-1">{helper}</p>}
    </div>
  );
}
